import dayjs from '@disco/utils/dayjs'
import React, { useCallback } from 'react'
import isEqual from 'react-fast-compare'
import type { Playlist, PlaylistId } from 'types/playlist'
import { getAlbumImage } from '@disco/utils/playlists'
import AlbumArtwork from 'components/common/album-artwork'
import type { LinkComponentType } from 'components/ui/grid-item'
import GridItem from 'components/ui/grid-item'
import Icon from 'components/ui/icon'
import Lozenge from 'components/ui/lozenge'
import Tag from 'components/ui/tag'
import TextOverflow from 'components/ui/text-overflow'
type Props = {
  album: Playlist
  color?: string | null | undefined
  image?: string | null | undefined
  link?: LinkComponentType
  overlayActions?: React.ReactNode
  overlayVisible?: boolean
  status?: string | null | undefined
  onClick?: (id: PlaylistId) => void
}

/**
 * Grid item component for displaying album information with artwork, title, and metadata
 * @param album - Album/playlist data to display
 * @param color - Override color for the album artwork background
 * @param image - Override image URL for the album artwork
 * @param link - Link component configuration for navigation
 * @param overlayActions - Actions to display in the overlay on hover
 * @param overlayVisible - Whether to show the overlay
 * @param status - Status text to display as a lozenge
 * @param onClick - Click handler for the grid item
 */
const AlbumGridItem = ({
  album,
  color,
  image,
  link,
  overlayActions,
  overlayVisible,
  status,
  onClick,
}: Props) => {
  const handleClick = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.preventDefault()

      if (onClick) {
        onClick(album['playlist_id'])
      }
    },
    [onClick, album['playlist_id']],
  )
  const overlay: React.ReactNode = (
    <GridItem.Overlay actions={overlayActions} visible={overlayVisible} />
  )
  const media: React.ReactNode = (
    <AlbumArtwork
      color={color === undefined ? album['color'] : color}
      image={image === undefined ? getAlbumImage(album) : image} // TODO: Refactor handling (overlay shouldn't render only if set to false)
      overlay={overlayActions === false ? null : overlay}
      status={status && <Lozenge color="yellow" text={status} />}
    />
  )
  return (
    <GridItem
      link={link}
      media={media}
      title={album['name'] || 'Untitled album'}
      caption={
        album['artist'] &&
        album['artist']['id'] &&
        album['artist']['name'] && (
          <i translate="no">
            <TextOverflow content={album['artist']['name']} />
          </i>
        )
      }
      tagline={
        album['release_date'] && (
          <Tag
            icon={<Icon width={12} height={12} type="calendar" />}
            tag={dayjs(album['release_date']).format('MMMM D, YYYY')}
          />
        )
      }
      onClick={onClick ? handleClick : undefined}
    />
  )
}

export default React.memo<Props>(AlbumGridItem, isEqual)
